import { ApiUnauthorizedError } from '@shared/api/errors';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { reportAppError } from '@shared/reportAppError';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  AdminRole,
  CheckAuthResponse,
  LoginResponse,
} from '../auth/apiHelpers';
import * as apiHelpers from '../auth/apiHelpers';
import { ROOT_PATH } from '../paths';
import * as restaurantApiHelpers from '../restaurants/apiHelpers';
import type { RestaurantMetadata } from '../restaurants/apiHelpers';

const LAST_VIEWED_RESTAURANT_ID_KEY =
  'peakRestaurantAdminLastViewedRestaurantId';

export interface AuthContextState {
  adminId?: string;
  adminName?: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginAndSetRestaurants: (
    email: string,
    password: string,
  ) => Promise<LoginResponse>;
  refreshRestaurants: () => void;
  restaurants: RestaurantMetadata[];
  selectedRestaurant?: RestaurantMetadata;
  selectedRestaurantId?: string;
  isManager: boolean;
  isSuperAdmin: boolean;
  updateSelectedRestaurantId: (id: string) => void;
  logout: (onSuccess: () => void) => void;
}

export const AuthContext = createContext<AuthContextState | null>(null);
AuthContext.displayName = 'Auth';

export const useAuth = () => useDefinedContext(AuthContext);

interface ObjectWithId {
  id: string;
}

const toMapWithIdAsKey = <T extends ObjectWithId>(
  arr: T[],
): Record<string, T> =>
  arr.reduce<Record<string, T>>((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {});

export const AuthContextProvider = ({ children }: { children?: ReactNode }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [adminId, setAdminId] = useState<string>('');
  const [adminName, setAdminName] = useState<string>('');
  const [isManager, setIsManager] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRestaurantsLoading, setIsRestaurantsLoading] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>();
  const [restaurants, setRestaurants] = useState<
    Record<string, RestaurantMetadata>
  >({});

  const setDefaultSelectedRestaurantId = (
    adminsRestaurants: RestaurantMetadata[] = [],
  ) => {
    // Provides support for the iPad app to automatically set the selected
    // restaurant when opening a page via a webview
    if (searchParams.has('restaurantId')) {
      const idFromSearchParam = searchParams.get('restaurantId')!;
      updateSelectedRestaurantId(idFromSearchParam);
      return;
    }
    // Most of our routes do not have a :restaurantId param
    // due to legacy product decisions. We are going to fight
    // 🦷 and 💅 to get identifiers into URL path segments.
    if (params.restaurantId) {
      updateSelectedRestaurantId(params.restaurantId);
      return;
    }
    const lastViewedRestaurantId =
      localStorage.getItem(LAST_VIEWED_RESTAURANT_ID_KEY) ?? undefined;

    const hasLastViewedRestaurantId = adminsRestaurants.some(
      (r) => r.id === lastViewedRestaurantId,
    );
    if (hasLastViewedRestaurantId) {
      setSelectedRestaurantId(lastViewedRestaurantId);
    } else {
      const firstRestaurantId = adminsRestaurants[0]?.id;
      setSelectedRestaurantId(firstRestaurantId);
      localStorage.setItem(LAST_VIEWED_RESTAURANT_ID_KEY, firstRestaurantId);
    }
  };

  const updateSelectedRestaurantId = (id: string): void => {
    setSelectedRestaurantId(id);
    localStorage.setItem(LAST_VIEWED_RESTAURANT_ID_KEY, id);
  };

  const setAdminRole = (
    role: AdminRole | undefined,
    isSuperAdmin: boolean,
  ): void => {
    setIsManager(role === 'manager' || isSuperAdmin);
    setIsSuperAdmin(isSuperAdmin);
  };

  const { isPending: isCheckAuthLoading } = useAbortEffect(
    {
      effect: async (signal) => {
        let authData: CheckAuthResponse;
        let restaurantsData: RestaurantMetadata[] = [];
        try {
          authData = await apiHelpers.checkAuth(signal);
          restaurantsData =
            await restaurantApiHelpers.getAdminRestaurants(signal);
        } catch (e) {
          if (e instanceof ApiUnauthorizedError) {
            return;
          }
          throw e;
        }
        const restaurantMap = toMapWithIdAsKey(restaurantsData);
        setRestaurants(restaurantMap);
        setDefaultSelectedRestaurantId(restaurantsData);
        setIsAuthenticated(authData.isAuthenticated);
        setAdminId(authData.id);
        setAdminName(authData.fullName || '');
        setAdminRole(authData.role, authData.isSuperAdmin);
      },
      throwOnError: true,
    },
    [],
  );

  const loginAndSetRestaurants = async (
    email: string,
    password: string,
  ): Promise<LoginResponse> => {
    const metadata = await apiHelpers.login(email, password);
    const restaurantsData = await restaurantApiHelpers.getAdminRestaurants();
    const restaurantMap = toMapWithIdAsKey(restaurantsData);
    setRestaurants(restaurantMap);
    setDefaultSelectedRestaurantId(restaurantsData);
    setIsAuthenticated(true);
    setAdminId(metadata.id);
    setAdminName(metadata.fullName || '');
    setAdminRole(metadata.role, metadata.isSuperAdmin);
    return metadata;
  };

  const logout = (onSuccess: () => void) => {
    void (async () => {
      try {
        await apiHelpers.signOut();
      } catch (e) {
        reportAppError(e);
        return;
      }
      localStorage.clear();
      queryClient.clear();
      setIsAuthenticated(false);
      onSuccess();
      navigate(ROOT_PATH);
    })();
  };

  const refreshRestaurants = () => {
    setIsRestaurantsLoading(true);
    void (async () => {
      try {
        const response = await restaurantApiHelpers.getAdminRestaurants();
        const restaurantMap = toMapWithIdAsKey(response);
        setRestaurants(restaurantMap);
        setDefaultSelectedRestaurantId(response);
      } finally {
        setIsRestaurantsLoading(false);
      }
    })();
  };

  // derived state
  const selectedRestaurant = selectedRestaurantId
    ? restaurants[selectedRestaurantId]
    : undefined;
  const restaurantValues = Object.values(restaurants);

  const isLoading = isCheckAuthLoading || isRestaurantsLoading;

  const value = useMemo<AuthContextState>(
    () => ({
      adminId,
      adminName,
      isAuthenticated,
      isLoading,
      loginAndSetRestaurants,
      refreshRestaurants,
      restaurants: restaurantValues,
      selectedRestaurant,
      selectedRestaurantId,
      isManager,
      isSuperAdmin,
      updateSelectedRestaurantId,
      logout,
    }),
    [
      adminId,
      adminName,
      isLoading,
      isAuthenticated,
      selectedRestaurantId,
      selectedRestaurant,
      restaurants,
      isManager,
      isSuperAdmin,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
